import * as React from "react";

import { Button, Divider, notification, Spin, Steps } from "antd";
import {
  Genre,
  Keyword,
  Language,
  Person,
  SaveMoviePrediction
} from "movieapi";
import * as MovieApi from "movieapi";
import AuthService from "src/helpers/AuthService";
import ChooseCast from "./ChooseCast";
import ChooseCrew from "./ChooseCrew";
import ChooseGenres from "./ChooseGenres";
import ChooseKeywords from "./ChooseKeywords";
import ChooseLanguages from "./ChooseLanguages";
import ChooseMovie from "./ChooseMovie";
import ChooseRole from "./ChooseRole";
import GeneralInformation from "./GeneralInformation";
import ShowPrediction from "./ShowPrediction";

const Step = Steps.Step;

export interface IPredictionStepsState {
  current: number;
  data: MovieApi.MovieInfoOutput;
  loadingPrediction: boolean;
  prediction: MovieApi.Prediction | undefined;
}

export interface IPredictionStepsProps {
  initialState?: {
    data: MovieApi.MovieInfoOutput;
    prediction: MovieApi.Prediction;
  };
}

/**
 * The initial state for the PredictionSteps
 */
const initialState: IPredictionStepsState = {
  current: 0,
  data: {},
  loadingPrediction: false,
  prediction: undefined
};

export default class PredictionSteps extends React.Component<
  IPredictionStepsProps,
  IPredictionStepsState
> {
  constructor(props: IPredictionStepsProps) {
    super(props);

    this.state = this.props.initialState
      ? { ...initialState, ...this.props.initialState }
      : initialState;

    // bind functions
    this.next = this.next.bind(this);
    this.prev = this.prev.bind(this);
    this.prediction = this.prediction.bind(this);
    this.handleMovieInfoChange = this.handleMovieInfoChange.bind(this);
    this.handleLanguageChange = this.handleLanguageChange.bind(this);
    this.handleKeywordChange = this.handleKeywordChange.bind(this);
    this.handleGenreChange = this.handleGenreChange.bind(this);
    this.handleDirectorChange = this.handleDirectorChange.bind(this);
    this.handleEditorChange = this.handleEditorChange.bind(this);
    this.handleWriterChange = this.handleWriterChange.bind(this);
    this.handleProducerChange = this.handleProducerChange.bind(this);
    this.handleCrewChange = this.handleCrewChange.bind(this);
    this.handleCastChange = this.handleCastChange.bind(this);
    this.handleBudgetChange = this.handleBudgetChange.bind(this);
    this.handleHomePageChange = this.handleHomePageChange.bind(this);
    this.handleRuntimeChange = this.handleRuntimeChange.bind(this);
    this.handleCastSizeChange = this.handleCastSizeChange.bind(this);
    this.handleCrewSizeChange = this.handleCrewSizeChange.bind(this);
    this.handleReleaseDateChange = this.handleReleaseDateChange.bind(this);
    this.savePrediction = this.savePrediction.bind(this);
  }

  public render() {
    const { current } = this.state;

    const steps = [
      {
        content: (
          <div style={{ padding: "10px" }}>
            <ChooseMovie
              data={this.state.data}
              handleMovieChange={this.handleMovieInfoChange}
            />
          </div>
        ),
        title: "Movie"
      },
      {
        content: (
          <div style={{ padding: "10px" }}>
            <GeneralInformation
              budget={this.state.data.budget}
              handleBudgetChange={this.handleBudgetChange}
              homepage={this.state.data.homepage}
              handleHomePageChange={this.handleHomePageChange}
              runtime={this.state.data.runtime}
              handleRuntimeChange={this.handleRuntimeChange}
              castSize={this.state.data.cast_size}
              handleCastSizeChange={this.handleCastSizeChange}
              crewSize={this.state.data.crew_size}
              handleCrewSizeChange={this.handleCrewSizeChange}
              releaseDate={this.state.data.release_date}
              handleReleaseDateChange={this.handleReleaseDateChange}
            />
          </div>
        ),
        title: "General"
      },
      {
        content: (
          <div style={{ padding: "10px" }}>
            <ChooseLanguages
              data={this.state.data.languages}
              handleLanguageChange={this.handleLanguageChange}
            />
            <Divider />
            <ChooseKeywords
              data={this.state.data.keywords}
              handleKeywordChange={this.handleKeywordChange}
            />
            <Divider />
            <ChooseGenres
              data={this.state.data.genres}
              handleGenreChange={this.handleGenreChange}
            />
          </div>
        ),
        title: "Theme"
      },
      {
        content: (
          <div style={{ padding: "10px" }}>
            <ChooseRole
              data={this.state.data.directors}
              handlePersonChange={this.handleDirectorChange}
              role={"Director"}
            />
            <Divider />
            <ChooseRole
              data={this.state.data.editors}
              handlePersonChange={this.handleEditorChange}
              role={"Editor"}
            />
            <Divider />
            <ChooseRole
              data={this.state.data.producers}
              handlePersonChange={this.handleProducerChange}
              role={"Producer"}
            />
            <Divider />
            <ChooseRole
              data={this.state.data.writers}
              handlePersonChange={this.handleWriterChange}
              role={"Writer"}
            />
            <Divider />
            <ChooseCrew
              data={this.state.data.crew}
              handlePersonChange={this.handleCrewChange}
            />
          </div>
        ),
        title: "Crew"
      },
      {
        content: (
          <div style={{ padding: "10px" }}>
            <ChooseCast
              data={this.state.data.cast}
              handlePersonChange={this.handleCastChange}
            />
          </div>
        ),
        title: "Cast"
      },
      {
        content: (
          <Spin spinning={this.state.loadingPrediction}>
            <ShowPrediction
              prediction={this.state.prediction}
              handleSavePrediction={this.savePrediction}
            />
          </Spin>
        ),
        title: "Prediction"
      }
    ];

    const handleStepClick = (
      e: React.MouseEvent<HTMLSpanElement>,
      index: number
    ) => {
      this.setState({ current: index });
    };

    // tslint:disable-next-line: variable-name
    const customDot = (dot: any, status_index: any) => {
      const onStepClick = (e: React.MouseEvent<HTMLSpanElement>) => {
        handleStepClick(e, status_index.index);
      };

      return (
        <span onClick={onStepClick} style={{ cursor: "pointer" }}>
          {dot}
        </span>
      );
    };

    return (
      <div>
        <Steps current={current} progressDot={customDot}>
          {steps.map(item => (
            <Step key={item.title} title={item.title} />
          ))}
        </Steps>
        <div className="steps-content">{steps[current].content}</div>
        <div className="steps-action">
          {current < steps.length - 1 && (
            <Button type="primary" onClick={this.next}>
              Next
            </Button>
          )}
          {current === steps.length - 1 && (
            <Button type="primary" onClick={this.prediction}>
              Prediction
            </Button>
          )}
          {current > 0 && (
            <Button style={{ marginLeft: 8 }} onClick={this.prev}>
              Previous
            </Button>
          )}
        </div>
      </div>
    );
  }

  /**
   * Called to bring the user to the next step
   * @param event
   */
  private next(event: React.MouseEvent<HTMLElement>) {
    this.setState(prevState => {
      return { current: prevState.current + 1 };
    });
  }

  /**
   * Called to bring the user to the previous step
   * @param event
   */
  private prev(event: React.MouseEvent<HTMLElement>) {
    this.setState(prevState => {
      return { current: prevState.current - 1 };
    });
  }

  /**
   * Called when the user is done
   * @param event
   */
  /**
   * Called when the user is done
   * @param event
   */
  private prediction(event: React.MouseEvent<HTMLElement>) {
    const api = new MovieApi.PredictionApi(
      new AuthService().getConfiguration(),
      process.env.REACT_APP_BASE_URL
    );
    this.setState({ loadingPrediction: true });
    api.getPredictionForMovie(this.state.data).then(result => {
      this.setState({ prediction: result, loadingPrediction: false });
    });
  }

  private savePrediction(predictionName: string) {
    const api = new MovieApi.PredictionApi(
      new AuthService().getConfiguration(),
      process.env.REACT_APP_BASE_URL
    );
    if (this.state.prediction) {
      const predictionData: SaveMoviePrediction = {
        prediction_name: predictionName,
        ...this.state.data,
        ...this.state.prediction
      };
      api
        .saveAPredictionForAMovie(predictionData)
        .then(result => {
          this.indicateSavePredictionSuccess(predictionName);
        })
        .catch(res => {
          (res as Response).json().then(data => {
            this.indicateSavePredictionFailure(predictionName, data.message);
          });
        });
    }
  }

  private indicateSavePredictionSuccess(predictionName: string) {
    notification.success({
      description: `Saved prediction ${predictionName}`,
      message: "Successfully Saved Prediction"
    });
  }

  private indicateSavePredictionFailure(
    predictionName: string,
    reason: string
  ) {
    notification.error({
      description: `${reason}`,
      message: "Failed to save prediction"
    });
  }

  private handleMovieInfoChange(movieInfo: MovieApi.MovieInfoOutput) {
    if (movieInfo) {
      this.setState({ data: movieInfo });
    }
  }

  private handleLanguageChange(languages: Language[]) {
    if (languages) {
      this.setState(prevState => {
        return { data: { ...prevState.data, languages } };
      });
    }
  }

  private handleKeywordChange(keywords: Keyword[]) {
    if (keywords) {
      this.setState(prevState => {
        return { data: { ...prevState.data, keywords } };
      });
    }
  }

  private handleGenreChange(genres: Genre[]) {
    if (genres) {
      this.setState(prevState => {
        return { data: { ...prevState.data, genres } };
      });
    }
  }

  private handleDirectorChange(directors: Person[]) {
    if (directors) {
      this.setState(prevState => {
        return { data: { ...prevState.data, directors } };
      });
    }
  }
  private handleEditorChange(editors: Person[]) {
    if (editors) {
      this.setState(prevState => {
        return { data: { ...prevState.data, editors } };
      });
    }
  }
  private handleWriterChange(writer: Person[]) {
    if (writer) {
      this.setState(prevState => {
        return { data: { ...prevState.data, writer } };
      });
    }
  }
  private handleProducerChange(producers: Person[]) {
    if (producers) {
      this.setState(prevState => {
        return { data: { ...prevState.data, producers } };
      });
    }
  }

  private handleCrewChange(crew: Person[]) {
    if (crew) {
      this.setState(prevState => {
        return { data: { ...prevState.data, crew } };
      });
    }
  }

  private handleCastChange(cast: Person[]) {
    if (cast) {
      this.setState(prevState => {
        return { data: { ...prevState.data, cast } };
      });
    }
  }

  private handleBudgetChange(budget: number) {
    if (budget) {
      this.setState(prevState => {
        return { data: { ...prevState.data, budget } };
      });
    }
  }

  private handleRuntimeChange(runtime: number) {
    if (runtime) {
      this.setState(prevState => {
        return { data: { ...prevState.data, runtime } };
      });
    }
  }

  private handleHomePageChange(homepage: string) {
    this.setState(prevState => {
      return { data: { ...prevState.data, homepage } };
    });
  }

  private handleCastSizeChange(castSize: number) {
    this.setState(prevState => {
      return { data: { ...prevState.data, cast_size: castSize } };
    });
  }

  private handleCrewSizeChange(crewSize: number) {
    this.setState(prevState => {
      return { data: { ...prevState.data, crew_size: crewSize } };
    });
  }

  private handleReleaseDateChange(releaseDate: string) {
    this.setState(prevState => {
      return { data: { ...prevState.data, release_date: releaseDate } };
    });
  }
}



// WEBPACK FOOTER //
// ./src/components/PredictionPage/PredictionSteps.tsx